// src/pages/Settings.jsx — Zerodha credentials + access token management
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

export default function Settings() {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { checkKiteStatus } = useAuth();

  // ── Credentials form state ──────────────────────────────────────────────
  const [creds, setCreds] = useState({
    api_key: '',
    api_secret: '',
    zerodha_user_id: '',
    zerodha_password: '',
    totp_secret: '',
  });
  const [credStatus, setCredStatus] = useState(null); // { api_key, has_api_secret, … }
  const [savingCreds, setSavingCreds] = useState(false);
  const [credsMsg, setCredsMsg] = useState(null);

  // ── Token section state ─────────────────────────────────────────────────
  const [loginUrl, setLoginUrl] = useState('');
  const [requestToken, setRequestToken] = useState('');
  const [currentToken, setCurrentToken] = useState('');
  const [generatingToken, setGeneratingToken] = useState(false);
  const [autoRefreshing, setAutoRefreshing] = useState(false);
  const [tokenMsg, setTokenMsg] = useState(null);

  // ── Data fetch section state ─────────────────────────────────────────────
  const [fetchingInstruments, setFetchingInstruments] = useState(false);
  const [instrumentsMsg, setInstrumentsMsg] = useState(null);
  const [fetchSymbols, setFetchSymbols] = useState('');
  const [fetchDays, setFetchDays] = useState(200);
  const [fetchingDaily, setFetchingDaily] = useState(false);
  const [dailyResults, setDailyResults] = useState(null);

  // ── Load existing credential metadata on mount ──────────────────────────
  useEffect(() => {
    authFetch('/api/user/credentials')
      .then((r) => r.json())
      .then((d) => {
        setCredStatus(d);
        // Pre-fill api_key and zerodha_user_id (non-secret fields)
        setCreds((prev) => ({
          ...prev,
          api_key: d.api_key || '',
          zerodha_user_id: d.zerodha_user_id || '',
        }));
      })
      .catch(() => {});

    authFetch('/api/user/token/current')
      .then((r) => r.json())
      .then((d) => setCurrentToken(d.access_token || ''))
      .catch(() => {});
  }, []);

  // Fetch login URL only after api_key is known
  useEffect(() => {
    if (!credStatus?.api_key) return;
    authFetch('/api/user/token/login-url')
      .then((r) => r.json())
      .then((d) => setLoginUrl(d.login_url || ''))
      .catch(() => {});
  }, [credStatus?.api_key]);

  // ── Auto-complete OAuth: detect request_token in URL after Kite redirect ──
  // Zerodha redirects back to /settings?request_token=xxx&action=login&status=success
  const generateFromUrl = useCallback(async (rt) => {
    setGeneratingToken(true);
    setTokenMsg({ type: 'success', msg: 'Zerodha login detected — generating token…' });
    try {
      const res  = await authFetch(`/api/user/token/generate?request_token=${encodeURIComponent(rt)}`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Token generation failed');
      setCurrentToken(data.access_token);
      const refreshed = await authFetch('/api/user/credentials').then(r => r.json());
      setCredStatus(refreshed);
      setTokenMsg({ type: 'success', msg: 'Access token generated and saved automatically.' });
      // Clean the URL so refresh doesn't re-trigger
      navigate('/settings', { replace: true });
    } catch (err) {
      setTokenMsg({ type: 'error', msg: err.message });
    } finally {
      setGeneratingToken(false);
    }
  }, [navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const rt     = params.get('request_token');
    const status = params.get('status');
    if (rt && status === 'success') generateFromUrl(rt);
  }, [location.search, generateFromUrl]);

  // ── Save credentials ────────────────────────────────────────────────────
  const handleSaveCreds = async (e) => {
    e.preventDefault();
    setSavingCreds(true);
    setCredsMsg(null);

    // Only send fields that are filled in (don't overwrite with blank)
    const payload = {};
    if (creds.api_key) payload.api_key = creds.api_key;
    if (creds.api_secret) payload.api_secret = creds.api_secret;
    if (creds.zerodha_user_id) payload.zerodha_user_id = creds.zerodha_user_id;
    if (creds.zerodha_password) payload.zerodha_password = creds.zerodha_password;
    if (creds.totp_secret) payload.totp_secret = creds.totp_secret;

    try {
      const res = await authFetch('/api/user/credentials', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');

      // Refresh metadata
      const refreshed = await authFetch('/api/user/credentials').then((r) => r.json());
      setCredStatus(refreshed);

      // Clear secret fields after save
      setCreds((prev) => ({
        ...prev,
        api_secret: '',
        zerodha_password: '',
        totp_secret: '',
      }));
      setCredsMsg({ type: 'success', msg: 'Credentials saved successfully.' });
      checkKiteStatus(); // update KiteBanner across the app
    } catch (err) {
      setCredsMsg({ type: 'error', msg: err.message });
    } finally {
      setSavingCreds(false);
    }
  };

  // ── Auto-refresh token (TOTP-based, one click) ──────────────────────────
  const handleAutoRefresh = async () => {
    setAutoRefreshing(true);
    setTokenMsg(null);
    try {
      const res = await authFetch('/api/user/token/auto-refresh', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Auto-refresh failed');
      // Reload the stored token display
      const tokenRes = await authFetch('/api/user/token/current').then(r => r.json());
      setCurrentToken(tokenRes.access_token || '');
      const refreshed = await authFetch('/api/user/credentials').then(r => r.json());
      setCredStatus(refreshed);
      setTokenMsg({ type: 'success', msg: data.message || 'Token refreshed successfully.' });
    } catch (err) {
      setTokenMsg({ type: 'error', msg: err.message });
    } finally {
      setAutoRefreshing(false);
    }
  };

  // ── Generate access token ───────────────────────────────────────────────
  const handleGenerateToken = async () => {
    if (!requestToken.trim()) {
      setTokenMsg({ type: 'error', msg: 'Paste the request_token first.' });
      return;
    }
    setGeneratingToken(true);
    setTokenMsg(null);
    try {
      const res = await authFetch(
        `/api/user/token/generate?request_token=${encodeURIComponent(requestToken.trim())}`,
        { method: 'POST' },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Token generation failed');
      setCurrentToken(data.access_token);
      setRequestToken('');
      setTokenMsg({ type: 'success', msg: 'Access token generated and saved.' });
    } catch (err) {
      setTokenMsg({ type: 'error', msg: err.message });
    } finally {
      setGeneratingToken(false);
    }
  };

  // ── Fetch NSE instruments ────────────────────────────────────────────────
  const handleFetchInstruments = async () => {
    setFetchingInstruments(true);
    setInstrumentsMsg(null);
    try {
      const res  = await authFetch('/api/data/fetch-instruments', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to fetch instruments');
      setInstrumentsMsg({ type: 'success', msg: `Fetched and stored ${data.count} NSE EQ instruments.` });
    } catch (err) {
      setInstrumentsMsg({ type: 'error', msg: err.message });
    } finally {
      setFetchingInstruments(false);
    }
  };

  // ── Fetch daily OHLC data ────────────────────────────────────────────────
  const handleFetchDaily = async () => {
    const symbols = fetchSymbols.split(/[\s,]+/).map(s => s.trim().toUpperCase()).filter(Boolean);
    if (!symbols.length) {
      setDailyResults({ error: 'Enter at least one symbol.' });
      return;
    }
    setFetchingDaily(true);
    setDailyResults(null);
    try {
      const res  = await authFetch('/api/data/fetch-daily', {
        method: 'POST',
        body: JSON.stringify({ symbols, days: Number(fetchDays) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Fetch failed');
      setDailyResults(data);
    } catch (err) {
      setDailyResults({ error: err.message });
    } finally {
      setFetchingDaily(false);
    }
  };

  // ── Helpers ─────────────────────────────────────────────────────────────
  const StatusBadge = ({ ok, label }) => (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 12,
      fontSize: '0.78rem',
      fontWeight: 600,
      backgroundColor: ok ? '#e8f5e9' : '#fff3e0',
      color: ok ? '#2e7d32' : '#e65100',
      marginLeft: 8,
    }}>
      {ok ? `${label} set` : `${label} not set`}
    </span>
  );

  return (
    <div style={{ maxWidth: 640, margin: '2rem auto', padding: '0 1rem' }}>
      <h1 style={{ color: '#1976d2' }}>Settings</h1>

      {/* ── Current status badges ── */}
      {credStatus && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '0.4rem',
          marginBottom: '1.5rem', padding: '0.75rem 1rem',
          backgroundColor: '#f5f5f5', borderRadius: 8,
        }}>
          <StatusBadge ok={!!credStatus.api_key}          label="API Key" />
          <StatusBadge ok={credStatus.has_api_secret}     label="API Secret" />
          <StatusBadge ok={!!credStatus.zerodha_user_id}  label="Zerodha User ID" />
          <StatusBadge ok={credStatus.has_zerodha_password} label="Zerodha Password" />
          <StatusBadge ok={credStatus.has_totp_secret}    label="TOTP Secret" />
          <StatusBadge ok={credStatus.has_access_token}   label="Access Token" />
        </div>
      )}

      {/* ── Credentials form ── */}
      <section style={s.section}>
        <h2 style={s.sectionTitle}>Zerodha API Credentials</h2>
        <p style={s.hint}>
          Secret fields are encrypted before storage. Leave a field blank to keep the existing value.
        </p>
        <form onSubmit={handleSaveCreds}>
          <Field label="API Key" name="api_key" value={creds.api_key}
            onChange={(v) => setCreds((f) => ({ ...f, api_key: v }))} />
          <Field label="API Secret" name="api_secret" type="password"
            value={creds.api_secret}
            placeholder={credStatus?.has_api_secret ? '(already set — enter to replace)' : ''}
            onChange={(v) => setCreds((f) => ({ ...f, api_secret: v }))} />
          <Field label="Zerodha User ID" name="zerodha_user_id"
            value={creds.zerodha_user_id}
            onChange={(v) => setCreds((f) => ({ ...f, zerodha_user_id: v }))} />
          <Field label="Zerodha Password" name="zerodha_password" type="password"
            value={creds.zerodha_password}
            placeholder={credStatus?.has_zerodha_password ? '(already set — enter to replace)' : ''}
            onChange={(v) => setCreds((f) => ({ ...f, zerodha_password: v }))} />
          <Field label="TOTP Secret" name="totp_secret" type="password"
            value={creds.totp_secret}
            placeholder={credStatus?.has_totp_secret ? '(already set — enter to replace)' : ''}
            onChange={(v) => setCreds((f) => ({ ...f, totp_secret: v }))} />

          {credsMsg && <Msg type={credsMsg.type} text={credsMsg.msg} />}

          <button type="submit" disabled={savingCreds} style={s.btn}>
            {savingCreds ? 'Saving…' : 'Save Credentials'}
          </button>
        </form>
      </section>

      {/* ── Data Fetch ── */}
      <section style={s.section}>
        <h2 style={s.sectionTitle}>Data Fetch</h2>
        <p style={s.hint}>
          Use your saved Kite credentials to pull data directly from Zerodha into the database.
        </p>

        {/* Step 1 — Instruments */}
        <div style={{ marginBottom: '1.25rem', padding: '1rem', backgroundColor: '#f0f4ff', borderRadius: 8 }}>
          <p style={{ margin: '0 0 0.4rem', fontWeight: 600 }}>Step 1 — Fetch NSE Instruments</p>
          <p style={{ margin: '0 0 0.75rem', fontSize: '0.88rem', color: '#555' }}>
            Downloads the full NSE EQ instrument list (symbol → token mapping) from Kite.
            Run this once before fetching daily data for any new symbol.
          </p>
          <button
            onClick={handleFetchInstruments}
            disabled={fetchingInstruments || !credStatus?.has_access_token}
            style={{ ...s.btn, backgroundColor: credStatus?.has_access_token ? '#1565c0' : '#aaa' }}
          >
            {fetchingInstruments ? 'Fetching…' : 'Fetch NSE Instruments'}
          </button>
          {!credStatus?.has_access_token && (
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.82rem', color: '#e65100' }}>
              Generate an access token above first.
            </p>
          )}
          {instrumentsMsg && <Msg type={instrumentsMsg.type} text={instrumentsMsg.msg} />}
        </div>

        {/* Step 2 — Daily OHLC */}
        <div style={{ padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: 8 }}>
          <p style={{ margin: '0 0 0.4rem', fontWeight: 600 }}>Step 2 — Fetch Daily OHLC Data</p>
          <p style={{ margin: '0 0 0.75rem', fontSize: '0.88rem', color: '#555' }}>
            Enter one or more symbols (comma or space separated) to fetch and store historical
            daily candles. Minimum 200 days is always fetched.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
            <input
              type="text"
              placeholder="e.g. INFY, TCS, RELIANCE"
              value={fetchSymbols}
              onChange={(e) => setFetchSymbols(e.target.value)}
              style={{ flex: '1 1 260px', padding: '0.6rem 0.75rem', fontSize: '0.95rem', border: '1px solid #ccc', borderRadius: 6 }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#444', whiteSpace: 'nowrap' }}>Days:</label>
              <input
                type="number"
                min={200}
                max={2000}
                value={fetchDays}
                onChange={(e) => setFetchDays(e.target.value)}
                style={{ width: 80, padding: '0.6rem 0.5rem', fontSize: '0.95rem', border: '1px solid #ccc', borderRadius: 6 }}
              />
            </div>
          </div>
          <button
            onClick={handleFetchDaily}
            disabled={fetchingDaily || !credStatus?.has_access_token}
            style={{ ...s.btn, backgroundColor: credStatus?.has_access_token ? '#1976d2' : '#aaa' }}
          >
            {fetchingDaily ? 'Fetching…' : 'Fetch Daily Data'}
          </button>

          {/* Results table */}
          {dailyResults && (
            dailyResults.error ? (
              <Msg type="error" text={dailyResults.error} />
            ) : (
              <div style={{ marginTop: '1rem', overflowX: 'auto' }}>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.85rem', color: '#555' }}>
                  Fetched {dailyResults.days}-day data:
                </p>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#e3f2fd' }}>
                      <th style={th}>Symbol</th>
                      <th style={th}>Status</th>
                      <th style={th}>Rows upserted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyResults.results.map((r) => (
                      <tr key={r.symbol} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={td}><strong>{r.symbol}</strong></td>
                        <td style={{ ...td, color: r.status === 'ok' ? '#2e7d32' : '#c62828' }}>
                          {r.status}
                          {r.detail && ` — ${r.detail}`}
                        </td>
                        <td style={td}>{r.rows ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      </section>

      {/* ── Kite access token ── */}
      <section style={s.section}>
        <h2 style={s.sectionTitle}>Kite Access Token</h2>

        {/* One-click auto-refresh (needs TOTP secret saved) */}
        <div style={{ marginBottom: '1.25rem', padding: '1rem', backgroundColor: '#e8f5e9', borderRadius: 8, border: '1px solid #c8e6c9' }}>
          <p style={{ margin: '0 0 0.4rem', fontWeight: 600, color: '#2e7d32' }}>Auto-Refresh (Recommended)</p>
          <p style={{ margin: '0 0 0.75rem', fontSize: '0.88rem', color: '#555' }}>
            Instantly refreshes your token using your saved TOTP secret — no browser login needed.
            Requires all five credentials (API Key, API Secret, Zerodha User ID, Password, TOTP Secret) to be saved above.
          </p>
          <button
            onClick={handleAutoRefresh}
            disabled={autoRefreshing || !credStatus?.has_totp_secret}
            style={{ ...s.btn, backgroundColor: credStatus?.has_totp_secret ? '#2e7d32' : '#aaa' }}
          >
            {autoRefreshing ? 'Refreshing…' : 'Auto-Refresh Token'}
          </button>
          {!credStatus?.has_totp_secret && (
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.82rem', color: '#e65100' }}>
              Save your TOTP Secret above to enable this.
            </p>
          )}
        </div>

        <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f0f4ff', borderRadius: 8 }}>
          <p style={{ margin: '0 0 0.5rem', fontWeight: 600 }}>Manual Login (if TOTP is not saved)</p>
          <p style={{ margin: '0 0 0.75rem', color: '#555', fontSize: '0.9rem' }}>
            Click the link below. After Zerodha login it will redirect back here and
            <strong> auto-generate your token</strong> — no copy-pasting needed.
          </p>
          {credStatus?.api_key ? (
            loginUrl ? (
              <a href={loginUrl} target="_blank" rel="noopener noreferrer"
                style={{ color: '#1976d2', fontWeight: 600 }}>
                Open Kite Login →
              </a>
            ) : (
              <span style={{ color: '#999' }}>Loading login URL…</span>
            )
          ) : (
            <span style={{ color: '#e65100' }}>Save your API Key first to get the login URL.</span>
          )}
        </div>

        <div style={{ padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: 8 }}>
          <p style={{ margin: '0 0 0.5rem', fontWeight: 600 }}>Step 2 — Paste Request Token</p>
          <input
            type="text"
            placeholder="Paste request_token here"
            value={requestToken}
            onChange={(e) => setRequestToken(e.target.value)}
            style={{ width: '100%', padding: '0.5rem', fontSize: '1rem', boxSizing: 'border-box', marginBottom: '0.75rem' }}
          />
          <button onClick={handleGenerateToken} disabled={generatingToken} style={s.btn}>
            {generatingToken ? 'Generating…' : 'Generate Access Token'}
          </button>
        </div>

        {tokenMsg && <Msg type={tokenMsg.type} text={tokenMsg.msg} />}

        <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: 8 }}>
          <p style={{ margin: '0 0 0.25rem', fontWeight: 600, fontSize: '0.9rem' }}>Current Access Token</p>
          <code style={{ wordBreak: 'break-all', color: '#333', fontSize: '0.85rem' }}>
            {currentToken
              ? `${currentToken.slice(0, 10)}${'•'.repeat(20)}${currentToken.slice(-4)}`
              : '(none stored)'}
          </code>
          {credStatus?.access_token_updated_at && (
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.78rem', color: '#888' }}>
              Last updated: {new Date(credStatus.access_token_updated_at).toLocaleString()}
            </p>
          )}
        </div>
      </section>
    </div>
  );
}

// ── Small reusable components ─────────────────────────────────────────────────

function Field({ label, name, value, onChange, type = 'text', placeholder = '' }) {
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#444', marginBottom: '0.25rem' }}>
        {label}
      </label>
      <input
        name={name}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: '0.6rem 0.75rem',
          fontSize: '0.95rem',
          border: '1px solid #ccc',
          borderRadius: 6,
          boxSizing: 'border-box',
        }}
      />
    </div>
  );
}

function Msg({ type, text }) {
  return (
    <div style={{
      margin: '0.75rem 0',
      padding: '0.6rem 1rem',
      borderRadius: 6,
      backgroundColor: type === 'success' ? '#e8f5e9' : '#ffebee',
      color: type === 'success' ? '#2e7d32' : '#c62828',
      fontSize: '0.9rem',
      fontWeight: 500,
    }}>
      {text}
    </div>
  );
}

const s = {
  section: {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: 10,
    padding: '1.5rem',
    marginBottom: '1.5rem',
  },
  sectionTitle: {
    margin: '0 0 0.5rem',
    fontSize: '1.1rem',
    color: '#333',
  },
  hint: {
    margin: '0 0 1rem',
    fontSize: '0.85rem',
    color: '#777',
  },
  btn: {
    marginTop: '0.5rem',
    padding: '0.65rem 1.5rem',
    backgroundColor: '#1976d2',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
};

const th = {
  padding: '0.4rem 0.75rem',
  textAlign: 'left',
  fontWeight: 600,
  borderBottom: '2px solid #bbdefb',
};

const td = {
  padding: '0.4rem 0.75rem',
};
